import { useEffect, useRef } from 'react';
import axios from 'axios';
import { WifiOff } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Silent global watcher for Tonk auto-drops.
 *
 * When the backend watchdog culls a disconnected player from an active
 * hand (see `game_loop.force_drop_seat`), it stamps a notice into
 * `tonk_auto_drop_notices`. This component polls
 * `/tonk/auto-dropped/latest` for the logged-in player and fires a
 * one-shot Sonner toast with a "Rejoin" button so a rage-quit / dead-tab
 * moment turns into a soft landing back at the table instead of the
 * player wondering what happened.
 *
 * The endpoint marks the notice cleared atomically on read, so the
 * toast will only ever appear once per auto-drop event no matter how
 * many tabs the user has open.
 */
export default function TonkAutoDropWatcher() {
    const { user } = useAuth();
    const nav = useNavigate();
    const firedIds = useRef(new Set());

    useEffect(() => {
        if (!user || user.role !== 'user') return undefined;
        let cancelled = false;

        const check = async () => {
            try {
                const { data } = await axios.get(
                    `${API}/tonk/auto-dropped/latest`,
                    { withCredentials: true },
                );
                if (cancelled) return;
                const n = data?.notice;
                if (!n || firedIds.current.has(n.id)) return;
                firedIds.current.add(n.id);
                // Suppress the toast if the player is CURRENTLY at ANY
                // Tonk table — they've clearly moved on from the dropped
                // one (or rejoined between the drop and the poll). A
                // pop-up about an old table would just be noise.
                const currentPath = window.location?.pathname || '';
                if (currentPath.startsWith('/casino/tonk/')) {
                    return;
                }
                const label = n.table_name || 'your Tonk table';
                toast.warning(
                    `You were auto-dropped from ${label}`,
                    {
                        duration: 12000,
                        description: n.table_still_open
                            ? 'The table is still going — hop back in?'
                            : 'That hand has already wrapped up.',
                        icon: <WifiOff className="w-4 h-4 text-amber-400" />,
                        action: n.table_still_open
                            ? {
                                label: 'Rejoin',
                                onClick: () => nav(`/casino/tonk/${n.table_id}?rejoin=1`),
                            }
                            : undefined,
                    },
                );
            } catch { /* silent — keep polling */ }
        };

        // First check ~1.8s after mount, then every 30s. A shorter interval
        // than Square receipt paths because "come back and rejoin" is time-sensitive.
        const t0 = setTimeout(check, 1800);
        const id = setInterval(check, 30_000);
        return () => { cancelled = true; clearTimeout(t0); clearInterval(id); };
    }, [user, nav]);

    return null;
}
